import React, { createContext, useContext, useState } from 'react'
import { JobModal } from './JobModal'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import { Job } from 'redux/actions/admin'

import useLocaleText from 'components/useLocaleText'

interface JobsModalsContextType {
  openJobModal: (job?: Job) => void
  requestCloseJobModal: () => void
  requestConfirmAction: (callback: () => void) => void
}

const JobsModalsContext = createContext<JobsModalsContextType | null>(null)

export const useJobsModals = () => {
  const ctx = useContext(JobsModalsContext)
  if (!ctx) throw new Error('useJobsModals must be used within JobsModalsProvider')
  return ctx
}

export const JobsModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const admin_cancel_confirm = useLocaleText('admin_cancel_confirm')
  const admin_cancel_desc = useLocaleText('card_confirm_desc')
  const admin_no_cancel = useLocaleText('no')
  const admin_yes_cancel = useLocaleText('yes')

  const change_job_status_confirm = useLocaleText('are_you_sure')
  const t_yes = useLocaleText('yes')
  const t_no = useLocaleText('no')

  const [jobModalOpen, setJobModalOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [actionConfirmationOpen, setActionConfirmationOpen] = useState(false)

  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  const openJobModal = (job?: Job) => {
    setSelectedJob(job || null)
    setJobModalOpen(true)
  }

  const onClose = () => {
    setJobModalOpen(false)
    setConfirmationOpen(false)
    setSelectedJob(null)
  }

  const requestCloseJobModal = () => {
    setConfirmationOpen(true)
  }

  const confirmClose = () => {
    setJobModalOpen(false)
    setSelectedJob(null)
    setConfirmationOpen(false)
  }

  const cancelClose = () => setConfirmationOpen(false)

  const requestConfirmAction = (callback: () => void) => {
    setPendingAction(() => callback)
    setActionConfirmationOpen(true)
  }

  const confirmAction = () => {
    if (pendingAction) {
      pendingAction()
    }
    setActionConfirmationOpen(false)
    setPendingAction(null)
    confirmClose()
  }

  const cancelAction = () => {
    setActionConfirmationOpen(false)
    setPendingAction(null)
  }

  return (
    <JobsModalsContext.Provider value={{ openJobModal, requestCloseJobModal, requestConfirmAction }}>
      {children}

      {jobModalOpen && (
        <JobModal isOpen job={selectedJob} requestCloseTagModal={requestCloseJobModal} onClose={onClose} />
      )}

      {confirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelClose}
          onConfirm={confirmClose}
          isLoading={false}
          title={admin_cancel_confirm}
          description={admin_cancel_desc}
          cancelButtonText={admin_no_cancel}
          confirmButtonText={admin_yes_cancel}
        />
      )}

      {actionConfirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelAction}
          onConfirm={confirmAction}
          isLoading={false}
          title={change_job_status_confirm}
          cancelButtonText={t_no}
          confirmButtonText={t_yes}
        />
      )}
    </JobsModalsContext.Provider>
  )
}
